import { InputAdornment } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { fixedChargesEditColumnsSelectors, getSelectorAttribute } from '@weft/app-selectors';
import type { Currency, PromotionInput, PromotionType } from '@weft/types';
import { DEFAULT_CURRENCY, PROMOTION_TYPES } from '@weft/types';
import { isNotNullOrUndefined, raiseUnreachable } from '@weft/utils';
import type { FieldPath } from 'react-hook-form';
import { getCurrencySymbol } from '../../../../../../services/currency-symbol-mapper';
import { FIXED_CHARGES_EDIT_COLUMNS_KEYS } from '../../FixedChargesEditTable/fixed-charges-edit-table-consts/fixed-charges-edit-columns';
import { PromotionChargeAddonsCell } from '../promotion-charges-edit-table-components/PromotionChargeAddonsCell';
import { PromotionEditCell } from '../promotion-charges-edit-table-components/PromotionEditCell';
import { RemovePromotionRow } from '../promotion-charges-edit-table-components/RemovePromotionRow';

export const PROMOTIONS_CHARGES_EDIT_COLUMNS_KEYS = Object({
  TYPE: 'promotionModel.promotionType',
  NAME: 'name',
  AMOUNT: 'customAmount',
  FEE: 'fee',
} satisfies Record<string, FieldPath<PromotionInput> | 'customAmount' | 'fee' | 'deleteRow'>);

export const PROMOTIONS_CHARGES_EDIT_COLUMNS: GridColDef<PromotionInput>[] = [
  {
    field: PROMOTIONS_CHARGES_EDIT_COLUMNS_KEYS.TYPE,
    headerName: 'Type',
    sortable: false,
    minWidth: 180,
    valueGetter: (params) => getPromotionTypeLabel(params.row.promotionModel.promotionType),
  },
  {
    field: PROMOTIONS_CHARGES_EDIT_COLUMNS_KEYS.NAME,
    headerName: 'Name',
    flex: 1,
    sortable: false,
    renderCell: ({ row, field }) => (
      <PromotionEditCell
        promotion={row}
        field={field as FieldPath<PromotionInput>}
        placeholder="Promotion name"
        tooltipPlacement="top-start"
        fullWidth
      />
    ),
  },
  {
    field: PROMOTIONS_CHARGES_EDIT_COLUMNS_KEYS.AMOUNT,
    headerName: 'Amount',
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => {
      const fieldPath = getFieldPath(row.promotionModel.promotionType);
      const prefix = getAmountPrefix(row.promotionModel.promotionType, DEFAULT_CURRENCY);

      return (
        <PromotionEditCell
          {...getSelectorAttribute(fixedChargesEditColumnsSelectors.amountInput)}
          promotion={row}
          field={fieldPath}
          valueAsNumber
          type="number"
          placeholder="0"
          tooltipPlacement="top-start"
          InputProps={{
            startAdornment: isNotNullOrUndefined(prefix) ? (
              <InputAdornment
                position="start"
                sx={{
                  paddingBottom: '2px',
                }}
              >
                {prefix}
              </InputAdornment>
            ) : undefined,
          }}
        />
      );
    },
  },
  {
    field: FIXED_CHARGES_EDIT_COLUMNS_KEYS.OTHER,
    headerName: 'Add on',
    headerClassName: 'pr-4',
    headerAlign: 'right',
    cellClassName: 'justify-end items-center',
    sortable: false,
    flex: 2,
    renderCell: ({ row }) => <PromotionChargeAddonsCell promotion={row}/>,
  },
  {
    field: 'deleteRow',
    headerName: '',
    cellClassName: 'justify-center',
    width: 30,
    renderCell: ({ row }) => (
      <RemovePromotionRow
        {...getSelectorAttribute(fixedChargesEditColumnsSelectors.removeRowButton)}
        promotion={row}
      />
    ),
  },
];

function getPromotionTypeLabel(promotionType: PromotionType) {
  switch (promotionType) {
    case PROMOTION_TYPES.FreeTrial:
      return 'Free Trial';
    case PROMOTION_TYPES.Credit:
      return 'Credit';
    case PROMOTION_TYPES.Percentage:
      return 'Percentage';
    default:
      return raiseUnreachable(promotionType);
  }
}

function getFieldPath(promotionType: PromotionType): FieldPath<PromotionInput> {
  switch (promotionType) {
    case PROMOTION_TYPES.FreeTrial:
      return 'promotionModel.promotionConfiguration.daysFree';
    case PROMOTION_TYPES.Credit:
      return 'promotionModel.promotionConfiguration.creditAmount';
    case PROMOTION_TYPES.Percentage:
      return 'promotionModel.promotionConfiguration.percentage';
    default:
      return raiseUnreachable(promotionType);
  }
}

function getAmountPrefix(promotionType: PromotionType, currency: Currency) {
  switch (promotionType) {
    case PROMOTION_TYPES.FreeTrial:
      return undefined;
    case PROMOTION_TYPES.Credit:
      return getCurrencySymbol(currency);
    case PROMOTION_TYPES.Percentage:
      return '%';
    default:
      return raiseUnreachable(promotionType);
  }
}